import { HTMLAttributes, forwardRef } from 'react'
import { cn } from '../../lib/utils'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'primary' | 'neutral'
  size?: 'xs' | 'sm' | 'md' | 'lg'
  dot?: boolean
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot, children, ...props }, ref) => {
    const variants = {
      default: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300',
      primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
      success: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
      warning: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
      danger: 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300',
      info: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300',
      neutral: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-300',
    }

    const sizes = {
      xs: 'px-1.5 py-0.5 text-[10px] rounded-full',
      sm: 'px-2 py-0.5 text-xs rounded-full',
      md: 'px-2.5 py-0.5 text-xs rounded-full',
      lg: 'px-3 py-1 text-sm rounded-full',
    }

    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1 font-medium',
          variants[variant],
          sizes[size],
          className
        )}
        {...props}
      >
        {dot && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />}
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'